import { Button, TableItem, TableMessage } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { Table } from 'components/organisms';
import { useFormikContext } from 'formik';
import { useNewWindowNavigate } from 'hooks/useNewWindowNavigate';
import { t } from 'i18next';
import { PiNavigationArrow, PiPlus, PiTrash } from 'react-icons/pi';
import { IClientAccountForm } from 'services/client-account/types';
import { useGetMusicProductPhysicalLightList } from 'services/music-product-physical/light';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';

const ClientAccountPhysicalMusicProducts = () => {
  const physicalMusicProductsLightList = useGetMusicProductPhysicalLightList();

  const { values, setFieldValue, status, initialStatus } =
    useFormikContext<IClientAccountForm>();

  const navigateToNewWindow = useNewWindowNavigate();

  const physicalMusicProductOptions = (physicalMusicProducts: string[]) =>
    physicalMusicProductsLightList.data
      ?.filter(
        physicalMusicProduct =>
          !physicalMusicProducts.includes(physicalMusicProduct.id)
      )
      .map(physicalMusicProduct => ({
        label: `${physicalMusicProduct.mainTitle.title ?? ''} - ${
          physicalMusicProduct.mainTitle.version ?? ''
        } (${physicalMusicProduct.ean})`,
        value: physicalMusicProduct.id,
      })) ?? [];

  const goToPhysicalMusicProduct = (id: string) => {
    navigateToNewWindow(`music-product-physical/${id}`);
  };

  return (
    <Table
      displayMode
      data={{
        nodes:
          values.physicalMusicProducts?.map((physicalMusicProduct, key) => ({
            id: key,
            value: physicalMusicProduct.id,
            label: `${physicalMusicProduct.name ?? ''} (${
              physicalMusicProduct.ean
            })`,
          })) ?? [],
      }}
      columns={[
        {
          label: t('input.label.music-product-physical'),
          renderCell: ({ label, value, id }) => (
            <TableItem>
              {status?.displayMode ? (
                <TableItem.Text text={label} />
              ) : (
                <FormInput.Select
                  name={`physicalMusicProducts[${id}].id`}
                  label={t('input.label.music-product-physical')}
                  options={physicalMusicProductOptions(
                    values.physicalMusicProducts?.map(
                      physicalMusicProduct => physicalMusicProduct.id
                    )
                  )}
                  onChange={option => {
                    setFieldValue(
                      `physicalMusicProducts[${id}].id`,
                      option.value
                    );
                    setFieldValue(
                      `physicalMusicProducts[${id}].name`,
                      option.label
                    );
                  }}
                  value={{ label: label ?? '-', value: value }}
                />
              )}
            </TableItem>
          ),
        },

        {
          label: '',
          renderCell: ({ value }) => (
            <TableItem justifyPosition='end'>
              {status?.displayMode ? (
                <Button
                  isClear
                  onlyIcon
                  onClick={() => goToPhysicalMusicProduct(value)}>
                  <Button.Icon
                    icon={<PiNavigationArrow transform='scale(-1,1)' />}
                  />
                </Button>
              ) : (
                <Button
                  variant={BUTTON_VARIANT.remove}
                  isClear
                  onlyIcon
                  onClick={() =>
                    setFieldValue(
                      `physicalMusicProducts`,
                      values.physicalMusicProducts?.filter(
                        physicalMusicProduct =>
                          physicalMusicProduct.id !== value
                      )
                    )
                  }>
                  <Button.Icon icon={<PiTrash />} />
                </Button>
              )}
            </TableItem>
          ),
        },
      ]}
      tableOptions={
        status?.displayMode
          ? undefined
          : {
              renderAfterTable: () => (
                <tfoot className='full-width-footer'>
                  <TableMessage
                    className='cursor-pointer'
                    disable={
                      values.physicalMusicProducts?.length
                        ? !values.physicalMusicProducts[
                            values.physicalMusicProducts.length - 1
                          ]?.id.length
                        : false
                    }
                    onClick={() =>
                      setFieldValue(
                        `physicalMusicProducts[${
                          values.physicalMusicProducts?.length ?? 0
                        }]`,
                        {
                          id: '',
                          name: '',
                        }
                      )
                    }>
                    <TableItem.Text text={t('button.add')} />
                    <PiPlus />
                  </TableMessage>
                </tfoot>
              ),
            }
      }
      size='compact'
    />
  );
};

export default ClientAccountPhysicalMusicProducts;
